import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Calendar, MessageSquare, Users, Mail, Globe, Archive, AlertTriangle } from "lucide-react";

const YahooGroupsHistory = () => {
  const timelineEvents = [
    {
      year: "1998",
      title: "eGroups Founded",
      description: "eGroups was founded by Scott Hassan and Carl Page as a free electronic mailing list service. It quickly grew to become one of the largest online community platforms.",
      icon: <Mail className="h-6 w-6" />
    },
    {
      year: "2000",
      title: "Yahoo Acquires eGroups",
      description: "Yahoo acquired eGroups for approximately $432 million in stock. The platform was rebranded as Yahoo Groups and integrated into Yahoo's growing web services ecosystem.",
      icon: <Globe className="h-6 w-6" />
    },
    {
      year: "2001",
      title: "Yahoo Groups Launch",
      description: "Yahoo Groups officially launched as a comprehensive online community platform, offering mailing lists, file sharing, photo albums, calendars, and message boards.",
      icon: <MessageSquare className="h-6 w-6" />
    },
    {
      year: "2005-2010",
      title: "Peak Usage Era",
      description: "Yahoo Groups reached its peak with millions of active communities covering topics from hobbies and education to professional networking and local community organizing.",
      icon: <Users className="h-6 w-6" />
    },
    {
      year: "2013",
      title: "Major Redesign",
      description: "Yahoo introduced a controversial redesign that removed many features and changed the user interface. Many long-time users criticized the changes and began migrating to other platforms.",
      icon: <AlertTriangle className="h-6 w-6" />
    },
    {
      year: "2019",
      title: "Content Deletion Announced",
      description: "Yahoo announced that all content uploaded to Yahoo Groups (files, photos, attachments, links, polls, and member data) would be permanently deleted on December 14, 2019.",
      icon: <Archive className="h-6 w-6" />
    },
    {
      year: "2020",
      title: "Complete Shutdown",
      description: "Yahoo Groups was completely discontinued on December 15, 2020. All remaining functionality was removed, ending nearly two decades of online community service.",
      icon: <Calendar className="h-6 w-6" />
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The History of Yahoo Groups - From eGroups to Shutdown",
    "description": "Explore the complete history of Yahoo Groups from its founding as eGroups in 1998 through its acquisition by Yahoo and ultimate shutdown in 2020.",
    "url": "https://cramstudywith.me/yahoo-groups/history/",
    "datePublished": "2024-01-15",
    "dateModified": "2024-01-15",
    "author": {
      "@type": "Organization",
      "name": "CramStudyWith.me"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CramStudyWith.me",
      "url": "https://cramstudywith.me"
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>The History of Yahoo Groups - From eGroups to Shutdown | CramStudyWith.me</title>
        <meta name="description" content="Explore the complete history of Yahoo Groups from its founding as eGroups in 1998 through its acquisition by Yahoo and ultimate shutdown in 2020." />
        <meta name="keywords" content="Yahoo Groups history, eGroups, Yahoo Groups shutdown, Yahoo Groups timeline, online community history" />
        <link rel="canonical" href="https://cramstudywith.me/yahoo-groups/history/" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              The History of Yahoo Groups
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              From eGroups' founding in 1998 to its final shutdown in 2020 - explore the complete story of one of the internet's most influential online community platforms.
            </p>
            <Link to="/yahoo-groups/">
              <Button size="lg" variant="outline" className="gap-2">
                <ArrowRight className="h-5 w-5" />
                Find a Yahoo Groups Alternative
              </Button>
            </Link>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">Yahoo Groups Timeline</h2>
              <div className="space-y-8">
                {timelineEvents.map((event, index) => (
                  <Card key={index} className="relative overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-secondary" />
                    <CardHeader className="flex flex-row items-center gap-4">
                      <div className="p-3 rounded-full bg-primary/10 text-primary">
                        {event.icon}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-primary">{event.year}</div>
                        <CardTitle className="text-xl">{event.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{event.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center">What Made Yahoo Groups Special</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      Email-Based Communities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Yahoo Groups pioneered email-based community management, allowing members to participate in discussions entirely through email without visiting the website.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Archive className="h-5 w-5 text-primary" />
                      File & Photo Sharing
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Groups could store and share files, photos, and documents, creating valuable archives for communities ranging from genealogy research to fan clubs.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Group Calendars
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Integrated calendars helped communities organize events, meetings, and deadlines - a feature that was ahead of its time in the early 2000s.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-primary" />
                      Global Communities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      At its peak, Yahoo Groups hosted millions of communities worldwide, connecting people with shared interests across geographical boundaries.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Legacy Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">Yahoo Groups' Legacy</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Yahoo Groups was a pioneer in online community building. It helped millions of people connect over shared interests, from academic study groups to fan communities, from professional networks to neighborhood organizations.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                While Yahoo Groups is gone, its influence lives on in modern community platforms. For educational communities and study groups, <Link to="/yahoo-groups/" className="text-primary hover:underline">CramStudyWith.me offers modern alternatives</Link> with video collaboration, productivity tools, and features designed specifically for learning.
              </p>
              <Link to="/yahoo-groups/">
                <Button size="lg" className="gap-2">
                  Explore Yahoo Groups Alternatives
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default YahooGroupsHistory;